import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const DeliveryScheduling = ({ 
  deliveryOption, 
  onDeliveryOptionChange,
  selectedAddress,
  onAddressChange,
  addresses,
  scheduledDate,
  scheduledTime,
  onScheduleChange
}) => {
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    label: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phone: ''
  });

  const deliveryOptions = [
    {
      value: 'delivery',
      label: 'Home Delivery',
      icon: 'Truck',
      description: 'Get it delivered to your doorstep'
    },
    {
      value: 'pickup',
      label: 'Self Pickup',
      icon: 'Store',
      description: 'Pick up from kitchen location'
    }
  ];

  const timeSlots = [
    '11:00 AM - 12:00 PM',
    '12:00 PM - 1:00 PM',
    '1:00 PM - 2:00 PM',
    '6:00 PM - 7:00 PM',
    '7:00 PM - 8:00 PM',
    '8:00 PM - 9:00 PM'
  ];

  const handleAddAddress = () => {
    if (newAddress?.label && newAddress?.street && newAddress?.city) {
      const addressToAdd = {
        id: Date.now(),
        ...newAddress
      };
      onAddressChange(addressToAdd);
      setNewAddress({
        label: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        phone: ''
      });
      setShowAddressForm(false);
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-6">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="MapPin" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="text-lg md:text-xl font-heading font-bold text-foreground">
            Delivery & Scheduling
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Choose delivery method and preferred time
          </p>
        </div>
      </div>
      <div className="space-y-4 md:space-y-6">
        <div>
          <label className="block text-sm md:text-base font-medium text-foreground mb-3">
            Delivery Method
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {deliveryOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => onDeliveryOptionChange(option?.value)}
                className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-smooth ${
                  deliveryOption === option?.value
                    ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  deliveryOption === option?.value ? 'bg-primary/20' : 'bg-muted'
                }`}>
                  <Icon 
                    name={option?.icon} 
                    size={20} 
                    color={deliveryOption === option?.value 
                      ? 'var(--color-primary)' 
                      : 'var(--color-muted-foreground)'
                    } 
                  />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm md:text-base font-medium text-foreground mb-1">
                    {option?.label}
                  </div>
                  <div className="text-xs md:text-sm text-muted-foreground">
                    {option?.description}
                  </div>
                </div>
                {deliveryOption === option?.value && (
                  <Icon name="CheckCircle2" size={20} color="var(--color-primary)" />
                )}
              </button>
            ))}
          </div>
        </div>

        {deliveryOption === 'delivery' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm md:text-base font-medium text-foreground">
                Delivery Address
              </label>
              <button
                onClick={() => setShowAddressForm(!showAddressForm)}
                className="text-sm text-primary hover:underline transition-smooth"
              >
                {showAddressForm ? 'Cancel' : '+ Add New Address'}
              </button>
            </div>

            {showAddressForm ? (
              <div className="bg-muted rounded-lg p-4 space-y-3 mb-3">
                <Input
                  label="Address Label"
                  type="text"
                  placeholder="Home, Office, etc."
                  value={newAddress?.label}
                  onChange={(e) => setNewAddress({...newAddress, label: e?.target?.value})}
                />
                <Input
                  label="Street Address"
                  type="text"
                  placeholder="123 Main Street, Apt 4B"
                  value={newAddress?.street}
                  onChange={(e) => setNewAddress({...newAddress, street: e?.target?.value})}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="City"
                    type="text"
                    placeholder="New York"
                    value={newAddress?.city}
                    onChange={(e) => setNewAddress({...newAddress, city: e?.target?.value})}
                  />
                  <Input
                    label="State"
                    type="text"
                    placeholder="NY"
                    value={newAddress?.state}
                    onChange={(e) => setNewAddress({...newAddress, state: e?.target?.value})}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="ZIP Code"
                    type="text"
                    placeholder="10001"
                    value={newAddress?.zipCode}
                    onChange={(e) => setNewAddress({...newAddress, zipCode: e?.target?.value})}
                  />
                  <Input
                    label="Phone Number"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={newAddress?.phone}
                    onChange={(e) => setNewAddress({...newAddress, phone: e?.target?.value})}
                  />
                </div>
                <Button
                  variant="default"
                  fullWidth
                  onClick={handleAddAddress}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Add Address
                </Button>
              </div>
            ) : null}

            <div className="space-y-2">
              {addresses?.map((address) => (
                <button
                  key={address?.id}
                  onClick={() => onAddressChange(address)}
                  className={`w-full flex items-start gap-3 p-4 rounded-lg border-2 transition-smooth text-left ${
                    selectedAddress?.id === address?.id
                      ? 'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selectedAddress?.id === address?.id ? 'bg-primary/20' : 'bg-muted'
                  }`}>
                    <Icon 
                      name="MapPin" 
                      size={20} 
                      color={selectedAddress?.id === address?.id 
                        ? 'var(--color-primary)' 
                        : 'var(--color-muted-foreground)'
                      } 
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm md:text-base font-medium text-foreground mb-1">
                      {address?.label}
                    </div>
                    <div className="text-xs md:text-sm text-muted-foreground line-clamp-2">
                      {address?.street}, {address?.city}, {address?.state} {address?.zipCode}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {address?.phone}
                    </div>
                  </div>
                  {selectedAddress?.id === address?.id && (
                    <Icon name="CheckCircle2" size={20} color="var(--color-primary)" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {deliveryOption === 'pickup' && (
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Store" size={20} color="var(--color-primary)" />
              <div>
                <div className="text-sm md:text-base font-medium text-foreground mb-1">
                  Pickup Location
                </div>
                <div className="text-xs md:text-sm text-muted-foreground">
                  456 Oak Avenue, Brooklyn, NY 11201
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Available: Mon-Sat, 11:00 AM - 9:00 PM
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          <Input
            label="Preferred Date"
            type="date"
            value={scheduledDate}
            onChange={(e) => onScheduleChange('date', e?.target?.value)}
            min={new Date()?.toISOString()?.split('T')?.[0]}
          />
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Preferred Time Slot
            </label>
            <select
              value={scheduledTime}
              onChange={(e) => onScheduleChange('time', e?.target?.value)}
              className="w-full h-10 px-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm md:text-base"
            >
              <option value="">Select time slot</option>
              {timeSlots?.map((slot) => (
                <option key={slot} value={slot}>
                  {slot}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryScheduling;